import Vue from 'vue'
import { subPageField } from '~/mixins/subpageType'
import kpiMeasures from '~/config/kpiMeasures.js'
import { logger } from '~/utils/logger'

//deep copy function to make copy of measures object
const deepCopyFunction = inObject => {
  let outObject, value, key

  if (typeof inObject !== 'object' || inObject === null) {
    return inObject // Return the value if inObject is not an object
  }

  // Create an array or object to hold the values
  outObject = Array.isArray(inObject) ? [] : {}

  for (key in inObject) {
    value = inObject[key]

    // Recursively (deep) copy for nested objects, including arrays
    outObject[key] = deepCopyFunction(value)
  }

  return outObject
}
//Hypercube props must be listed in data so that they are reactive

const calcUnitType = type => {
  let unitType
  switch (type) {
    case 'citywide':
      unitType = 'Overall'
      break
    case 'school':
      unitType = 'School'
      break
    case 'network':
      unitType = 'Network'
      break
    case 'sector':
      unitType = 'Sector'
      break
  }
  return unitType
}

export const state = () => ({
  page: {},
  loadingKpi: true,
  hypercubes: [
    {
      hc_id: '',
      hc_name: '',
      hypercube: {},
      layout: {},
      values: []
    }
  ],
  chartsViewBy: {
    climate: {
      regularAttendanceByStudentGroup: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      zeroSuspensionsByStudentGroup: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      seriousIncidentRate: {
        viewBy: 'Grade',
        column: 'Grade'
      }
    },
    mathScience: {
      keystone_math_proficient: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      keystone_sci_proficient: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      pssa_math_proficient: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      pssa_sci_proficient: {
        viewBy: 'Grade',
        column: 'Grade'
      }
    },
    reading: {
      keystone_literature_proficient: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      pssa_literature_proficient: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      reading_t1: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      growth: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      reading_OnTarget: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      pssa_ELA: {
        viewBy: 'Ethnicity',
        column: 'Ethnicity'
      }
    },
    collegeCareer: {
      NGOT: {
        viewBy: 'Ethnicity',
        column: 'Ethnicity'
      },
      FYG: {
        viewBy: 'Ethnicity',
        column: 'Ethnicity'
      },
      FFCM: {
        viewBy: 'Ethnicity',
        column: 'Ethnicity'
      },
      CP: {
        viewBy: 'Ethnicity',
        column: 'Ethnicity'
      }
    },
    surveyResults: {
      studentResponseRate: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      parentResponseRate: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      climate_q1: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      climate_q2: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      climate_q3: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      climate_q4: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      instruction_q1: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      instruction_q2: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      instruction_q3: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      instruction_q4: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      community_q1: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      community_q2: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      community_q3: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      community_q4: {
        viewBy: 'Grade',
        column: 'Grade'
      }
    },
    /* Enrolled (current year) by Student Group (Grade, Gender, Ethnicity, EL, IEPm EconDisadvantage, age bands). Stacked bars show in/out of catchment */
    demographics: {
      enrollmentByStudentGroup: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      enrollmentPctByStudentGroup: {
        viewBy: 'Grade',
        column: 'Grade'
      },
      catchmentByStudentGroup: {
        viewBy: 'Grade',
        column: 'Grade'
      }
    }
  },
  list_primaryHomeLanguage: [],
  list_primaryDisability: [],
  homepageHypercube: []
})

export const mutations = {
  kpisLoaded(state, status) {
    state.loadingKpi = status
  },
  setPromise(state) {
    state.promise = 'resolved'
  },
  setPageInfo(state, page) {
    state.page = { ...page }
  },
  setChartView(state, data) {
    state.chartsViewBy[data.page][data.chart].column = data.column
    state.chartsViewBy[data.page][data.chart].viewBy = data.viewBy
  },
  setHyperCubes(state, data) {
    state.hypercubes = data
  },
  setHomepageHypercube(state, data) {
    state.homepageHypercube = data
  },
  updateHyperCube(state, data) {
    for (let i = 0; i < state.hypercubes.length; i++) {
      //must use Vue.set here so that the change is reactive - otherwise this falls under caveat 1 of Vue's reactivity
      Vue.set(state.hypercubes, i, {
        ...state.hypercubes[i],
        values: data[i].values,
        layout: data[i].layout
      })
    }
  },
  update_list_primaryHomeLanguage(state, data) {
    state.list_primaryHomeLanguage = data.sort((a, b) => {
      const va = parseInt(a.value)
      const vb = parseInt(b.value)
      return va < vb ? 1 : va === vb ? (a.label > b.label ? 1 : -1) : -1
    })
  },
  update_list_primaryDisability(state, data) {
    state.list_primaryDisability = data.sort((a, b) => {
      const va = parseInt(a.value)
      const vb = parseInt(b.value)
      return va < vb ? 1 : va === vb ? (a.label > b.label ? 1 : -1) : -1
    })
  },
  setFilterAnalysis(state, data) {
    state.page = {
      ...state.page,
      filterSetAnalysis: ', ' + state.page.typeSA + data
    }
  },
  setSprFilterAnalysis(state, data) {
    state.page = { ...state.page, SprFilterAnalysis: data }
  },
  updateStudentDenominator(state, value) {
    state.page.showKpi = value
  },
  clearSubpageValues(state) {
    state.page = {}
  }
}

export const getters = {
  pageInfo: state => {
    return state.page
  },
  get_chartsViewBy: state => {
    return state.chartsViewBy
  },
  getkpivalue: state => measureName => {
    const hcs = state.hypercubes
    for (let i = 0; i < hcs.length; i++) {
      let measures = hcs[i].hypercube.qHyperCubeDef.qMeasures
      logger.debug(`Measure: ${measureName} in hypercube ${hcs[i].hc_name}`)
      let measureIdx = measures.findIndex(e => e.qDef.qLabel === measureName)
      logger.debug(`Measure index: ${measureIdx}`)
      if (measureIdx > -1) {
        measureIdx += hcs[i].hypercube.qHyperCubeDef.qDimensions.length //the position of the measure in the results returned
        logger.debug(`Adjusted measure index: ${measureIdx}`)
        let value = 0
        //hcs[i].values[ra][measureIdx].qNum
        for (let ra = 0; ra < hcs[i].values.length; ra++) {
          logger.debug(`Row ${ra}: ${JSON.stringify(hcs[i].values[ra])}`)
          logger.debug(
            `Value at measure index: ${hcs[i].values[ra][measureIdx].qNum}`
          )
          logger.debug(
            `Text at measure index: ${hcs[i].values[ra][measureIdx].qText}`
          )
          if (!isNaN(hcs[i].values[ra][measureIdx].qNum)) {
            value += hcs[i].values[ra][measureIdx].qNum
          } else return hcs[i].values[ra][measureIdx].qText
        }
        logger.debug(`Returning value for measure ${measureName}: ${value}`)
        return value
      }
    }
  },
  gethomepagekpivalue: state => measureName => {
    const hcs = state.homepageHypercube
    for (let i = 0; i < hcs.length; i++) {
      let measures = hcs[i].hypercube.qHyperCubeDef.qMeasures
      let measureIdx = measures.findIndex(e => e.qDef.qLabel == measureName)
      if (measureIdx > -1) {
        measureIdx += hcs[i].hypercube.qHyperCubeDef.qDimensions.length //the position of the measure in the results returned
        let value = 0
        //hcs[i].values[ra][measureIdx].qNum
        for (let ra = 0; ra < hcs[i].values.length; ra++) {
          if (!isNaN(hcs[i].values[ra][measureIdx].qNum)) {
            value += hcs[i].values[ra][measureIdx].qNum
          } else return hcs[i].values[ra][measureIdx].qText
        }
        return value
      }
    }
  },
  list_primaryHomeLanguage: state => {
    return state.list_primaryHomeLanguage
  },
  list_primaryDisability: state => {
    return state.list_primaryDisability
  }
}

export const actions = {
  //used to control dimension used in charts with dynamic dimensions.
  changeView({ commit }, data) {
    commit('setChartView', data)
  },

  //get_pageInfo returns information about the selected page to the state, creates set analysis to apply accountable flags.
  async get_pageInfo({ commit, rootGetters }, data) {
    const slug = data.slug //slug
    let type = rootGetters['schools/slugToType'](slug) //type

    let page = {
      type,
      typeField: subPageField(type),
      minStudentDenominator: 20,
      slugvalue: slug
    }

    if (type === 'school') {
      page = {
        ...page,
        slugvalue: slug,
        slugid: rootGetters['schools/slugToId'](slug), //id
        name: rootGetters['schools/slugToName'](slug), //name
        displayName: rootGetters['schools/slugToDisplayName'](slug), //displayName
        schoolLevel: rootGetters['schools/slugToSchoolLevel'](slug), //schoolLevel
        sector: rootGetters['schools/slugToSector'](slug), //sector
        openedYear: rootGetters['schools/slugToOpenedYear'](slug),
        closedYear: rootGetters['schools/slugToClosedYear'](slug)
      }
    } else if (type !== 'citywide') {
      let typeGetterName = type[0].toUpperCase() + type.substring(1)
      page = {
        ...page,
        slugvalue: rootGetters[`schools/slugToName${typeGetterName}`](slug), //name as slugvalue - non school level,
        slugid: rootGetters[`schools/slugToId${typeGetterName}`](slug), //id
        name: rootGetters[`schools/slugToName${typeGetterName}`](slug), //name
        displayName: rootGetters[`schools/slugToDisplayName${typeGetterName}`](
          slug
        ) //displayName
      }
      if (
        page.slugvalue === 'opportunity' ||
        page.slugvalue === 'Alternative'
      ) {
        //TG051720 - Added to allow overview to change at sector & ntwk views for opportunity ntwk and alt sector
        page.sector = 'Alternative'
      }
    } else if (type === 'citywide') {
      page = {
        ...page,
        displayName: 'All Schools' //displayName
      }
    }
    if (type === 'citywide') {
      page.typeSA = 'City_Accountable_Flag={1}'
    } else if (type === 'sector') {
      page.typeSA = 'Sector_Accountable_Flag={1}'
    } else if (type === 'network') {
      page.typeSA = 'Network_Accountable_Flag={1}'
    } else if (type === 'studyArea') {
      page.typeSA = 'StudyArea_Accountable_Flag={1}'
    } else {
      page.typeSA = 'School_Accountable_Flag={1}'
    }

    commit('setPageInfo', page)
  },

  //get_dimensions is called on mount of any subpage, get_dimensions returns most of the dynamic text values shown on the overview subpage (IE: Address, Phone #, study area, etc...)
  async get_dimensions({ commit, state }, { data, engine }) {
    //const data = payload.data
    //const engine = await payload.engine

    const pageTypeField = state.page.typeField

    const hypercube = {
      qInfo: { qType: 'visualization' },
      qHyperCubeDef: {
        qDimensions: [
          //#region Overview
          {
            qDef: {
              qFieldDefs: [pageTypeField]
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current StreetAddress']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current City']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current State']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current ZipCode']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current PhoneNumber']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current FaxNumber']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current URL']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current GradeSpan']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current AdmissionType']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current SchoolLeader']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current FACELiason']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current FACELiason PhoneNumber']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current FACELiason Email']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current AssistantSuperintendent']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current City Council District']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current Network']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current ULCS Code']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Current PA ID']
            }
          },
          {
            qDef: {
              qFieldDefs: ['CSPR Area Name']
            }
          },
          {
            qDef: {
              qFieldDefs: ['Flag_Has_PreK']
            }
          },
          {
            qDef: {
              qFieldDefs: ['IsCommunitySchool']
            }
          }
          //#endregion
        ],
        qInitialDataFetch: [{ qWidth: 100, qHeight: 50 }]
      }
    }

    let overviewResults = []

    commit('subpage/overview/clearDimensions', '', {
      root: true
    })

    const hypercubeSession = await engine.createSessionObject(hypercube)

    if (pageTypeField !== 'citywide') {
      await hypercubeSession.selectHyperCubeValues(
        '/qHyperCubeDef',
        0,
        [parseInt(data.slugid)],
        false
      )
    }

    const layout = await hypercubeSession.getLayout()
    const values = layout.qHyperCube.qDataPages[0].qMatrix
    for (let m = 0; m < values.length; m++) {
      const overviewData = {
        addressLine1: values[m][1].qText,
        addressLine2:
          values[m][2].qText +
          ', ' +
          values[m][3].qText +
          ' ' +
          values[m][4].qText,
        phone: values[m][5].qText,
        fax: values[m][6].qText,
        website: values[m][7].qText,
        gradeLevels: values[m][8].qText,
        admissions: values[m][9].qText,
        principal: values[m][10].qText,
        familyEngagement: values[m][11].qText,
        familyEngagementPhone: values[m][12].qText,
        familyEngagementEmail: values[m][13].qText,
        assistantSuperintendent: values[m][14].qText,
        cityCouncilDistrict: values[m][15].qText,
        network: values[m][16].qText,
        schoolCode: values[m][17].qText,
        paSchoolCode: values[m][18].qText,
        studyArea: values[m][19] ? values[m][19].qText : '',
        prek: values[m][20].qNum && values[m][20].qNum === 1 ? 'Yes' : 'No',
        communitySchool: values[m][21] ? values[m][21].qText : ''
      }
      overviewResults.push(overviewData)
    }

    const overviewResult = overviewResults[0]

    commit('subpage/overview/updateDimensions', overviewResult, {
      root: true
    })
  },

  //Called once on initial load of a subpage to get all lat long coordinates for each school represented in the selected subpage.
  async get_latlng({ commit, state }, { engine, data }) {
    //const data = payload.data
    //const engine = await payload.engine
    let pageTypeField = state.page.typeField

    const hypercube = {
      qInfo: { qType: 'visualization' },
      qHyperCubeDef: {
        qDimensions: [
          {
            qDef: {
              qFieldDefs: [pageTypeField]
            },
            qNullSuppression: true
          },
          {
            qDef: {
              qFieldDefs: ['Current GPS Location']
            },
            qNullSuppression: true
          },
          {
            qDef: {
              qFieldDefs: ['Current School Name for Display'],
              qSortCriterias: [{ qSortByLoadOrder: 0, qSortByAscii: 1 }]
            },
            qNullSuppression: true
          }
        ],
        qInitialDataFetch: [{ qWidth: 3, qHeight: 500 }]
      }
    }

    let results = []
    const hypercubeSession = await engine.createSessionObject(hypercube)

    let idx = 0

    if (pageTypeField !== 'citywide') {
      await hypercubeSession.selectHyperCubeValues(
        '/qHyperCubeDef',
        0,
        [parseInt(data.slugid)],
        false
      )
      idx += 1
    }

    const layout = await hypercubeSession.getLayout()
    const values = layout.qHyperCube.qDataPages[0].qMatrix
    for (let m = 0; m < values.length; m++) {
      const data = {
        position: {
          lat: Number(values[m][0 + idx].qText.split(',')[0]),
          lng: Number(values[m][0 + idx].qText.split(',')[1])
        },
        schoolName: values[m][1 + idx] ? values[m][1 + idx].qText : ''
      }
      results.push(data)
    }

    const res = results
    commit('subpage/overview/updateLatlng', res, { root: true })
  },

  /**
   * Called initially by SSR for citywide, then called again on each
   * school/group selection.
   **/
  async get_kpis_filter({ commit, state, dispatch }, { engine, data }) {
    commit('kpisLoaded', true)
    //const engine = await payload.engine
    await engine.clearAll()

    //let data
    //let pageTypeField
    let pageInfo
    let saToAdd

    // will be undefined for ssr load
    if (data !== undefined) {
      //data = payload.data
      //pageTypeField = state.page.typeField
      pageInfo = { ...state.page, loading: true }

      saToAdd = data.typeSA

      commit('setPageInfo', pageInfo)
      if (pageInfo.typeField !== 'citywide') {
        //get the field the filter is selected on from the engine session
        const selectField = await engine.getField(pageInfo.typeField)
        //make a selection on the provided field using the selected field value from the filter
        await selectField.toggleSelect(pageInfo.slugvalue)
      }
    } else {
      saToAdd = 'City_Accountable_Flag={1}'
    }

    let dims = []
    let hypercubes = []
    //reset measures to non-modified versions by making a deep copy of original measures list
    let revisedMeasures = deepCopyFunction(kpiMeasures)
    const subjectMeasures = Object.keys(revisedMeasures)
    //this loop creates a hypercube for every set of measures (sectioned by subject area)
    for (let i = 0; i < subjectMeasures.length; i++) {
      //update all non-spr/aepr KPI measures with pageType SA
      if (subjectMeasures[i] != 'spr' && subjectMeasures[i] != 'aepr') {
        let currentMeasures = revisedMeasures[subjectMeasures[i]]
        //loop through measures in each subject domane and add new set analysis
        for (let j = 0; j < currentMeasures.length; j++) {
          let excludeMeasures = [
            'Total Schools',
            'District Total Schools',
            'Charter Total Schools',
            'Alternative Total Schools',
            'PSSA/Keystone ELA/Lit Performance Title',
            'PSSA/Keystone Math/Alg1 Performance Title',
            'Assistant Superintendents',
            'Elementary Total Schools',
            'Elementary-Middle Total Schools',
            'Elementary-High Total Schools',
            'Elementary-Middle-High Total Schools',
            'Middle Total Schools',
            'Middle-High Total Schools',
            'High Total Schools',
            'Transition Total Schools'
          ]

          let specialSaMeasures = [
            'Climate Avg Scaled Score (CY)',
            'Climate Avg Scaled Score (PY)',
            'Instruction Avg Scaled Score (CY)',
            'Instruction Avg Scaled Score (PY)',
            'Parent - School Relationship Avg Scaled Score (CY)',
            'Parent - School Relationship Avg Scaled Score (PY)'
          ]

          if (
            !excludeMeasures.includes(currentMeasures[j].qDef.qLabel) &&
            !specialSaMeasures.includes(currentMeasures[j].qDef.qLabel)
          ) {
            let str = currentMeasures[j].qDef.qDef
            let saStart = str.lastIndexOf('{<')
            if (saStart > 0) {
              let strPt1 = str.substr(0, saStart + 2)
              let strPt2 = str.substr(saStart + 2, str.length)
              let formattedSaToAdd = saToAdd + ', '
              revisedMeasures[subjectMeasures[i]][j].qDef.qDef =
                strPt1 + formattedSaToAdd + strPt2
            } else {
              let strPt1 = str.substr(0, str.lastIndexOf('(') + 1)
              let strPt2 = str.substr(str.lastIndexOf('(') + 1, str.length)
              let formattedSaToAdd = '{<' + saToAdd + '>}'
              revisedMeasures[subjectMeasures[i]][j].qDef.qDef =
                strPt1 + formattedSaToAdd + strPt2
            }
          } else if (
            specialSaMeasures.includes(currentMeasures[j].qDef.qLabel)
          ) {
            let specialSa = `unit_type={'${calcUnitType(state.page.type)}'}`

            let str = currentMeasures[j].qDef.qDef
            let saStart = str.lastIndexOf('{<')
            if (saStart > 0) {
              let strPt1 = str.substr(0, saStart + 2)
              let strPt2 = str.substr(saStart + 2, str.length)
              let formattedSaToAdd = specialSa + ', '
              revisedMeasures[subjectMeasures[i]][j].qDef.qDef =
                strPt1 + formattedSaToAdd + strPt2
            } else {
              let strPt1 = str.substr(0, str.lastIndexOf('(') + 1)
              let strPt2 = str.substr(str.lastIndexOf('(') + 1, str.length)
              let formattedSaToAdd = '{<' + specialSa + '>}'
              revisedMeasures[subjectMeasures[i]][j].qDef.qDef =
                strPt1 + formattedSaToAdd + strPt2
            }
          }
        }
      }

      hypercubes.push({
        hc_name: subjectMeasures[i],
        hypercube: {
          qInfo: { qType: 'visualization' },
          qHyperCubeDef: {
            qDimensions: dims,
            qMeasures: revisedMeasures[subjectMeasures[i]],
            qInitialDataFetch: [
              {
                qWidth: revisedMeasures[subjectMeasures[i]].length,
                qHeight: 1
              }
            ]
          }
        }
      })

      let subjectHypercube = hypercubes[i].hypercube
      let hypercubeSession = await engine.createSessionObject(subjectHypercube)

      let layout = await hypercubeSession.getLayout()

      let hcInfo = await hypercubeSession.getInfo()
      hypercubes[i].hc_id = hcInfo.qId
      hypercubes[i].layout = layout.qHyperCube
      hypercubes[i].values = layout.qHyperCube.qDataPages[0].qMatrix
    }
    if (process.server) {
      const homepageHypercube = hypercubes.filter(
        hc => hc.hc_name === 'overview'
      )

      commit('setHomepageHypercube', homepageHypercube)
    }

    commit('setHyperCubes', hypercubes) //calls setHyperCubes mutation which adds the returned hypercubes to the state
    if (data !== undefined) {
      pageInfo.loading = false
      commit('setPageInfo', pageInfo)

      await dispatch(
        'subpage/moreinfo/get_lists',
        {
          engine: engine,
          data: { slugid: pageInfo.slugid, type: pageInfo.type }
        },
        { root: true }
      )
    }
    commit('kpisLoaded', false)
    // Must be awaited (not fire-and-forget): callers that await
    // get_kpis_filter — including nuxtServerInit, which closes the engine
    // session once its dispatch chain resolves — need this to actually be
    // done before they continue, not just kicked off in the background.
    await dispatch('get_lists', { engine: await engine })
  },

  //get_lists returns # of students by HomeLanguage and PrimaryDisability, primarily used on Enrollment subpage.
  async get_lists({ commit }, { engine }) {
    //const engine = await payload.engine
    let homeLangugeResults = []
    const hypercube_primaryHomeLanguage = {
      qInfo: { qType: 'visualization' },
      qHyperCubeDef: {
        qDimensions: [
          {
            qDef: {
              qFieldDefs: ['HomeLanguage']
            },
            qNullSuppression: true
          }
        ],
        qMeasures: [
          {
            qSortBy: {
              qSortByNumeric: -1
            },
            qDef: {
              qLabel: 'Home Language Count',
              qDef: `=Sum({<[Flag_Oct1_CY]={1},[EL]={'Yes'}>} Enrolled_Oct1)`
            }
          }
        ],
        qInterColumnSortOrder: [3, 2],
        qInitialDataFetch: [{ qWidth: 2, qHeight: 500 }]
      }
    }
    const hc_session_primaryHomeLanguage = await engine.createSessionObject(
      hypercube_primaryHomeLanguage
    )
    const layout_primaryHomeLanguage = await hc_session_primaryHomeLanguage.getLayout()

    const primaryHomeLanguage_values =
      layout_primaryHomeLanguage.qHyperCube.qDataPages[0].qMatrix
    for (let m = 0; m < primaryHomeLanguage_values.length; m++) {
      const data = {
        label: primaryHomeLanguage_values[m][0].qText,
        value: primaryHomeLanguage_values[m][1].qText
      }
      homeLangugeResults.push(data)
    }

    commit('update_list_primaryHomeLanguage', homeLangugeResults)

    let iepResults = []
    const hypercube_iep = {
      qInfo: { qType: 'visualization' },
      qHyperCubeDef: {
        qDimensions: [
          {
            qDef: {
              qFieldDefs: ['PrimaryDisability']
            },
            qNullSuppression: true
          }
        ],
        qMeasures: [
          {
            qSortBy: {
              qSortByNumeric: -1
            },
            qDef: {
              qLabel: 'PrimaryDisability Count',
              qDef: `=Sum({<[IEP]={'Yes'},[Flag_Oct1_CY]={1}>} Enrolled_Oct1)`
            }
          }
        ],
        qInterColumnSortOrder: [3, 2],
        qInitialDataFetch: [{ qWidth: 2, qHeight: 500 }]
      }
    }
    const hc_session_iep = await engine.createSessionObject(hypercube_iep)
    const layout_iep = await hc_session_iep.getLayout()

    const iep_values = layout_iep.qHyperCube.qDataPages[0].qMatrix
    for (let m = 0; m < iep_values.length; m++) {
      const data = {
        label: iep_values[m][0].qText,
        value: iep_values[m][1].qText
      }
      iepResults.push(data)
    }

    commit('update_list_primaryDisability', iepResults)
  },

  //update_kpis is called everytime KPI hypercube values need to be updated - when filter selections are toggled or a hard reload is initiated when filters have been selected
  async update_kpis({ commit, state, dispatch }, { engine, data }) {
    commit('kpisLoaded', true)
    dispatch('get_lists', { engine: await engine })
    if (data === 'clearAllButPage') {
      //if the clearFilters method is what is calling this action then 'clearAllButPage' is passed in as data
      //when this happens, we want to clear all selections except those on the pageType set in the state
      //jv note: when this is citywide there isn't any field to keep, so clear all
      if (state.page.type !== 'citywide') {
        const clearAllButThisField = await engine.getField(
          subPageField(state.page.type)
        )
        await clearAllButThisField.clearAllButThis()
      } else {
        await engine.clearAll()
      }
    } else {
      //get the field the filter is selected on from the engine session
      const selectField = await engine.getField(data.filterType)
      //make a selection on the provided field using the selected field value from the filter
      await selectField.toggleSelect(data.value.name)
    }
    let hypercubes = [...state.hypercubes]
    let updatedHypercubes = []

    //get the new values of each hypercube from the engine
    for (let i = 0; i < hypercubes.length; i++) {
      //retrieve the hc from the current engine session
      let current_hc_id = await hypercubes[i].hc_id
      let current_hc = await engine.getObject(current_hc_id) //provides hypercube object interface
      //get the values of the hc after the selection has been toggled
      let current_layout = await current_hc.getLayout()
      let new_values = await current_layout.qHyperCube.qDataPages[0].qMatrix
      //add new set of values to updatedHypercubes array
      updatedHypercubes.push({ values: new_values, layout: current_layout })
    }

    //update hypercubes in the store to filtered values
    commit('updateHyperCube', updatedHypercubes)
    commit('kpisLoaded', false)
  },

  //updateFilterAnalysis is used to update the dynamic set analysis used in the qdtcomponent charts - because these hypercubes are managed differently we have to use this method of
  //dynamic set analysis to control selections on charts.
  updateFilterAnalysis({ commit, rootState }) {
    // Grades
    let grades = rootState.subpage.filters.filtersSelected.filter(
      el => el.filterType === 'Grade'
    )

    if (grades.length > 0) grades = grades.map(g => `'${g.value.name}'`)
    else grades = rootState.subpage.filters.grade.map(g => `'${g.name}'`)

    // Gender
    let genders = rootState.subpage.filters.filtersSelected.filter(
      el => el.filterType === 'Gender'
    )

    if (genders.length > 0) genders = genders.map(g => `'${g.value.name}'`)
    else {
      genders = rootState.subpage.filters.gender.map(g => `'${g.name}'`)
    }

    // Ethnicity
    let ethnicities = rootState.subpage.filters.filtersSelected.filter(
      el => el.filterType === 'Ethnicity'
    )

    if (ethnicities.length > 0)
      ethnicities = ethnicities.map(g => `'${g.value.name}'`)
    else {
      ethnicities = rootState.subpage.filters.ethnicity.map(g => `'${g.name}'`)
    }

    const filterSetAnalysis =
      ', [Grade]={' +
      grades +
      '}, [Gender]={' +
      genders +
      '}, [Ethnicity]={' +
      ethnicities +
      '}'

    commit('setFilterAnalysis', filterSetAnalysis)

    //SPR SA
    let reportTypes = rootState.subpage.filters.filtersSelected.filter(
      el => el.filterType === 'SPR.Report'
    )

    if (reportTypes.length > 0)
      reportTypes = reportTypes.map(g => `'${g.value.name}'`)
    else {
      reportTypes = rootState.subpage.filters.reportTypes.map(
        g => `'${g.name}'`
      )
    }

    const sprSetAnalysis = ', [SPR.Report]={' + reportTypes + '}'

    commit('setSprFilterAnalysis', sprSetAnalysis)
  },

  //clearValues is used on load of the homepage to clear all active selections from the current Qlik engine session and reset the state (so when a subpage is loaded it doesn't show
  //values for the previously loaded subpage)
  async clearValues({ commit }, engine) {
    await engine.clearAll()
    commit('setHyperCubes', {})
    commit('clearSubpageValues')
    commit('subpage/overview/clearOverviewValues', {}, { root: true })
  }
}
